import { useMemo, useState } from 'react';

import { useProductRepositories } from '~/hooks/useProducts';
import { useRepositories } from '~/hooks/useRepositories';
import type { StatusChip } from '~/utils/status';
import { CHIP_LABELS, CHIP_TO_STATUSES, FILTER_CHIPS, STATUS_DISPLAY_LABEL } from '~/utils/status';

import type { ProjectProps, Repository } from '~/types';

// --- Types ---

export type SortOption = 'status' | 'newest' | 'oldest';

/**
 * Re-exported from `~/utils/status` (the canonical status-display module) so
 * existing importers of this hook — `ProjectFilterBar.tsx`, `ProjectStatusChips.tsx`,
 * `List.tsx`, `ProductDetail.tsx`, `RepositoryDetail.tsx` — keep compiling
 * unchanged. See `~/utils/status` for the source of truth.
 */
export type { StatusChip };
export { CHIP_LABELS, CHIP_TO_STATUSES, FILTER_CHIPS, STATUS_DISPLAY_LABEL };

/** Sentinel key for the "Unassigned" product filter option. */
export const UNASSIGNED_PRODUCT_KEY = '__unassigned__';

/** Sentinel key for the "All products" reset option. */
export const ALL_PRODUCTS_KEY = '__all__';
export const ALL_REPOS_KEY = '__all_repos__';

// --- Storage ---

const STORAGE_SORT_KEY = 'projects-sort-option';
const STORAGE_FILTER_KEY = 'projects-status-filter';
const STORAGE_REPO_FILTER_KEY = 'projects-repository-filter';
const STORAGE_PRODUCT_FILTER_KEY = 'projects-product-filter';

const VALID_CHIPS = new Set<string>(FILTER_CHIPS);

/**
 * Read chip selection from localStorage with legacy migration.
 *
 * Legacy formats (single string):
 *   'all'                     → [] (show all)
 *   'SCOPING' | 'TASKING'    → ['TODO'] (collapsed into Todo chip)
 *   'TODO'|'IN_PROGRESS'|'DONE' → [value]
 *   unknown                   → []
 *
 * Current format: JSON array of StatusChip values, e.g. '["TODO","DONE"]'
 */
function readChipsFromStorage(): StatusChip[] {
  try {
    const stored = localStorage.getItem(STORAGE_FILTER_KEY);
    if (stored === null) return [];

    // Try JSON array first (current format)
    if (stored.startsWith('[')) {
      const parsed: unknown = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return parsed.filter((v): v is StatusChip => VALID_CHIPS.has(v as string));
      }
      return [];
    }

    // Legacy string migration
    if (stored === 'all') return [];
    if (stored === 'SCOPING' || stored === 'TASKING') return ['TODO'];
    if (VALID_CHIPS.has(stored)) return [stored as StatusChip];
    return [];
  } catch {
    return [];
  }
}

function readProductFilterFromStorage(): Set<string> {
  try {
    const stored = localStorage.getItem(STORAGE_PRODUCT_FILTER_KEY);
    if (stored === null) return new Set();
    const parsed: unknown = JSON.parse(stored);
    if (Array.isArray(parsed)) {
      return new Set(parsed.filter((v): v is string => typeof v === 'string'));
    }
    return new Set();
  } catch {
    return new Set();
  }
}

// --- Product filter storage ---

/**
 * Read repo filter from localStorage with migration from old single-select format.
 *
 * Legacy format (single string):
 *   'all'          → empty Set (show all)
 *   'unassigned'   → Set(['unassigned'])
 *   '<repo-id>'    → Set(['<repo-id>'])
 *
 * Current format: JSON array, e.g. '["repo-1","repo-2"]'
 */
function readRepoFilterFromStorage(): Set<string> {
  try {
    const stored = localStorage.getItem(STORAGE_REPO_FILTER_KEY);
    if (stored === null) return new Set();

    // Try JSON array first (new format)
    if (stored.startsWith('[')) {
      const parsed: unknown = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return new Set(parsed.filter((v): v is string => typeof v === 'string'));
      }
      return new Set();
    }

    // Legacy string migration
    if (stored === 'all') return new Set();
    // Any other string (including 'unassigned') becomes a single-element Set
    return new Set([stored]);
  } catch {
    return new Set();
  }
}

function writeChipsToStorage(chips: StatusChip[]): void {
  try {
    localStorage.setItem(STORAGE_FILTER_KEY, JSON.stringify(chips));
  } catch {
    // ignore quota errors
  }
}

// --- Repo filter storage (Set<string> with legacy migration) ---

function writeProductFilterToStorage(filter: Set<string>): void {
  try {
    localStorage.setItem(STORAGE_PRODUCT_FILTER_KEY, JSON.stringify([...filter]));
  } catch {
    // ignore quota errors
  }
}

function writeRepoFilterToStorage(filter: Set<string>): void {
  try {
    localStorage.setItem(STORAGE_REPO_FILTER_KEY, JSON.stringify([...filter]));
  } catch {
    // ignore quota errors
  }
}

// --- Sort ---

// SCOPING, TASKING, and TODO share the same weight (unified "Todo" group)
const statusValue: Record<string, number> = {
  IN_PROGRESS: 5,
  SCOPING: 4,
  TASKING: 4,
  TODO: 4,
  DONE: 1,
};

// --- Hook ---

export interface UseProjectFilteringReturn {
  availableRepos: Repository[];
  clearChips: () => void;
  clearProductFilter: () => void;
  clearRepositoryFilter: () => void;
  filterChips: StatusChip[];
  filteredProjects: ProjectProps[];
  isUnassignedProductSelected: boolean;
  productFilter: Set<string>;
  repositoryFilter: Set<string>;
  searchQuery: string;
  selectedChips: StatusChip[];
  setProductFilter: (filter: Set<string>) => void;
  setRepositoryFilter: (filter: Set<string>) => void;
  setSearchQuery: (query: string) => void;
  setSortOption: (option: SortOption) => void;
  sortOption: SortOption;
  sortedProjects: ProjectProps[];
  toggleChip: (chip: StatusChip) => void;
}

// --- Generic storage reader (for sort/repo filters) ---

function readStorage<T extends string>(key: string, fallback: T, validValues?: Set<string>): T {
  try {
    const stored = localStorage.getItem(key);
    if (stored === null) return fallback;
    if (validValues && !validValues.has(stored)) return fallback;
    return stored as T;
  } catch {
    return fallback;
  }
}

export function useProjectFiltering(
  projects: ProjectProps[],
  options?: { preScoped?: boolean; skipRepoFilter?: boolean },
): UseProjectFilteringReturn {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChips, setSelectedChips] = useState<StatusChip[]>(readChipsFromStorage);
  const [sortOption, setSortOption] = useState<SortOption>(() =>
    readStorage<SortOption>(STORAGE_SORT_KEY, 'newest', new Set(['newest', 'oldest'])),
  );
  const [productFilter, setProductFilterState] = useState<Set<string>>(
    readProductFilterFromStorage,
  );
  const [repositoryFilter, setRepositoryFilterState] =
    useState<Set<string>>(readRepoFilterFromStorage);

  const { data: repositories = [] } = useRepositories();

  const toggleChip = (chip: StatusChip): void => {
    setSelectedChips(prev => {
      const next = prev.includes(chip) ? prev.filter(c => c !== chip) : [...prev, chip];
      writeChipsToStorage(next);
      return next;
    });
  };

  const clearChips = (): void => {
    setSelectedChips([]);
    writeChipsToStorage([]);
  };

  const handleSortOption = (option: SortOption): void => {
    setSortOption(option);
    try {
      localStorage.setItem(STORAGE_SORT_KEY, option);
    } catch {
      // ignore quota errors
    }
  };

  const setProductFilter = (filter: Set<string>): void => {
    setProductFilterState(filter);
    writeProductFilterToStorage(filter);
  };

  const clearProductFilter = (): void => {
    setProductFilterState(new Set());
    writeProductFilterToStorage(new Set());
  };

  const setRepositoryFilter = (filter: Set<string>): void => {
    setRepositoryFilterState(filter);
    writeRepoFilterToStorage(filter);
  };

  const clearRepositoryFilter = (): void => {
    setRepositoryFilterState(new Set());
    writeRepoFilterToStorage(new Set());
  };

  // Cascading: when a product is selected, show only its repos.
  // Uses the product→repo API relationship (not derived through projects).
  const isUnassignedProductSelected = productFilter.has(UNASSIGNED_PRODUCT_KEY);

  // Only resolve a real product ID for repo cascading — ignore the unassigned sentinel
  const selectedProductId = useMemo(() => {
    if (productFilter.size === 0) return '';
    const first = [...productFilter][0];
    return first === UNASSIGNED_PRODUCT_KEY ? '' : first;
  }, [productFilter]);
  const { data: productRepos, isLoading: productReposLoading } =
    useProductRepositories(selectedProductId);

  // A repo is "claimed" if any product owns it via ProductRepository. Such repos are
  // never "unassigned" — even if a project on them has no direct productId, the repo
  // itself belongs to a product.
  const claimedRepoIds = useMemo(
    () => new Set(repositories.filter(r => (r.productIds?.length ?? 0) > 0).map(r => r.id)),
    [repositories],
  );

  const availableRepos = useMemo(() => {
    if (isUnassignedProductSelected) {
      // Show only repos that (a) are not claimed by any product AND (b) have at least
      // one project with no direct productId. A repo claimed by a product is never
      // "unassigned" regardless of whether any projects on it have productId set.
      const repoIdsWithTrulyUnassignedProjects = new Set(
        projects
          .filter(p => !p.productId && p.repositoryId && !claimedRepoIds.has(p.repositoryId))
          .map(p => p.repositoryId as string),
      );
      return repositories.filter(r => repoIdsWithTrulyUnassignedProjects.has(r.id));
    }
    if (!selectedProductId) return repositories;
    if (productRepos && productRepos.length > 0) return productRepos;
    return repositories;
  }, [
    selectedProductId,
    productRepos,
    repositories,
    isUnassignedProductSelected,
    projects,
    claimedRepoIds,
  ]);

  // Derive the effective repository filter.
  // - No product selected: use the user's manual repo selection directly.
  // - Product selected + user has picked specific repos: use the user's selection
  //   (constrained to repos that actually belong to the product).
  // - Product selected + no repos picked: default to all of the product's repos
  //   so the product selection alone narrows the list without requiring a repo pick.
  const effectiveRepositoryFilter = useMemo(() => {
    if (!selectedProductId || !productRepos || productRepos.length === 0) {
      return repositoryFilter;
    }
    const productRepoIds = new Set(productRepos.map(r => r.id));
    // If the user has selected repos, intersect with the product's repos
    if (repositoryFilter.size > 0) {
      const intersection = new Set([...repositoryFilter].filter(id => productRepoIds.has(id)));
      // If intersection is empty (user's saved repos don't overlap with this product),
      // fall back to all product repos to avoid a confusing empty state
      return intersection.size > 0 ? intersection : productRepoIds;
    }
    // No repo selection — show all of this product's repos
    return productRepoIds;
  }, [selectedProductId, productRepos, repositoryFilter]);

  // --- Sort ---

  const sortedProjects = useMemo(() => {
    return [...projects].sort((a, b) => {
      switch (sortOption) {
        case 'status':
          return (statusValue[b.status] ?? 0) - (statusValue[a.status] ?? 0);
        case 'newest':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'oldest':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        default:
          return 0;
      }
    });
  }, [projects, sortOption]);

  // --- Filtering pipeline: sorted → product → repo → chips ---

  // Filter by product: match projects that either have the productId directly
  // or belong to one of the product's repos.
  const productRepoIdSet = useMemo(
    () => new Set(productRepos?.map(r => r.id) ?? []),
    [productRepos],
  );

  const productFilteredProjects = useMemo(() => {
    // preScoped callers (e.g. ProductView, which already scoped `projects` to a
    // single product) skip product/repo/chip filtering entirely — a stale
    // product/repo/chip selection persisted in localStorage from the global
    // Projects page must never silently blank the pre-scoped board.
    if (options?.preScoped) return sortedProjects;
    if (productFilter.size === 0) return sortedProjects;
    // "Unassigned" → projects with no direct product AND whose repo (if any) is not
    // claimed by any product. A project on a product-owned repo is implicitly assigned
    // to that product even when its own productId column is null.
    if (isUnassignedProductSelected) {
      return sortedProjects.filter(
        p =>
          p.id.startsWith('temp-') ||
          (!p.productId && (!p.repositoryId || !claimedRepoIds.has(p.repositoryId))),
      );
    }
    // Don't filter while product repos are loading — prevents premature empty state
    // when filters are restored from localStorage before the API responds
    if (selectedProductId && productReposLoading) return sortedProjects;
    return sortedProjects.filter(
      p =>
        p.id.startsWith('temp-') ||
        (p.productId && productFilter.has(p.productId)) ||
        (p.repositoryId && productRepoIdSet.has(p.repositoryId)),
    );
  }, [
    sortedProjects,
    productFilter,
    productRepoIdSet,
    selectedProductId,
    productReposLoading,
    isUnassignedProductSelected,
    claimedRepoIds,
    options?.preScoped,
  ]);

  const repoFilteredProjects = useMemo(() => {
    // preScoped supersedes skipRepoFilter — it also skips repo filtering.
    if (options?.preScoped || options?.skipRepoFilter || effectiveRepositoryFilter.size === 0)
      return productFilteredProjects;
    return productFilteredProjects.filter(
      p =>
        p.id.startsWith('temp-') ||
        // Match by repositoryId
        (!!p.repositoryId && effectiveRepositoryFilter.has(p.repositoryId)) ||
        // Keep repoless projects only when a specific product is selected and
        // the project belongs to that product directly. In the Unassigned context,
        // repoless projects are only surfaced when no repo filter is active
        // (the effectiveRepositoryFilter.size === 0 early-return above handles that).
        (!p.repositoryId &&
          !isUnassignedProductSelected &&
          productFilter.size > 0 &&
          !!p.productId &&
          productFilter.has(p.productId)),
    );
  }, [
    productFilteredProjects,
    effectiveRepositoryFilter,
    options?.skipRepoFilter,
    options?.preScoped,
    productFilter,
    isUnassignedProductSelected,
  ]);

  const chipFilteredProjects = useMemo(() => {
    if (options?.preScoped) return repoFilteredProjects;
    if (selectedChips.length === 0) return repoFilteredProjects;
    const allowedStatuses = new Set(selectedChips.flatMap(chip => CHIP_TO_STATUSES[chip]));
    return repoFilteredProjects.filter(
      project => project.id.startsWith('temp-') || allowedStatuses.has(project.status),
    );
  }, [repoFilteredProjects, selectedChips, options?.preScoped]);

  const filteredProjects = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return chipFilteredProjects;
    return chipFilteredProjects.filter(
      p =>
        p.id.startsWith('temp-') ||
        p.title.toLowerCase().includes(q) ||
        p.creatorDisplayName?.toLowerCase().includes(q),
    );
  }, [chipFilteredProjects, searchQuery]);

  return {
    availableRepos,
    clearChips,
    clearProductFilter,
    clearRepositoryFilter,
    filterChips: FILTER_CHIPS,
    filteredProjects,
    isUnassignedProductSelected,
    productFilter,
    repositoryFilter: effectiveRepositoryFilter,
    searchQuery,
    selectedChips,
    setProductFilter,
    setRepositoryFilter,
    setSearchQuery,
    setSortOption: handleSortOption,
    sortOption,
    sortedProjects,
    toggleChip,
  };
}
